import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import { Worker, UpdateWorkerRequest } from '../../models/worker';
import { updateWorkerRequest } from '../../api/workersApi';

/**
 * Editable form for the NAW-document fields on `WorkerEntity` — bsn,
 * idDocument*, vca*, homeAddress. Lives in the Documents tab next to
 * NawDocumentSection so the recruiter can fill missing-readiness fields
 * without leaving the page.
 *
 * Bot-collected values (from VCA/ID upload + confirmation handlers) land in
 * these same columns, so when the worker has uploaded their docs via WhatsApp
 * the fields show up pre-populated and the recruiter just verifies them.
 *
 * Toggleable: defaults to collapsed when worker already satisfies readiness
 * (bsn + idDocument* all set), expanded when something is missing — so the
 * gap is visible at-a-glance.
 */
interface Props {
    worker: Worker;
    onSaved: () => void;
}

type IdType = NonNullable<Worker['idDocumentType']>;
type VcaSubType = NonNullable<Worker['vcaType']>;

const ID_TYPE_OPTIONS: { value: IdType; label: string }[] = [
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'ID_CARD', label: 'ID card' },
    { value: 'RESIDENCE_PERMIT', label: 'Residence permit' },
    { value: 'DRIVERS_LICENSE', label: "Driver's license" },
];

const VCA_TYPE_OPTIONS: { value: VcaSubType; label: string }[] = [
    { value: 'BASIS', label: 'VCA Basis' },
    { value: 'VOL', label: 'VCA VOL' },
    { value: 'VIL', label: 'VCA VIL' },
];

const dateString = (value: string | null | undefined): string => {
    if (!value) return '';
    return value.slice(0, 10);
};

const isBsnValid = (raw: string): boolean => /^\d{9}$/.test(raw.trim());

/**
 * Stable snapshot of all editable form fields. Used as the baseline for the
 * "is dirty" deep comparison — Save stays disabled until the current form
 * state differs from the initial snapshot. Built fresh whenever the worker
 * prop changes (so saving + parent refetch resets the baseline cleanly).
 */
function buildSnapshot(worker: Worker) {
    const home = worker.homeAddress ?? null;
    const current = (worker as any).currentAddress ?? null;
    return {
        bsn: '',
        idType: (worker.idDocumentType ?? '') as IdType | '',
        idNumber: worker.idDocumentNumber ?? '',
        idValidUntil: dateString(worker.idDocumentValidUntil),
        vcaType: (worker.vcaType ?? '') as VcaSubType | '',
        vcaNumber: worker.vcaNumber ?? '',
        vcaValidUntil: dateString(worker.vcaValidUntil),
        currentStreet: current?.street ?? '',
        currentHouseNumber: current?.houseNumber ?? '',
        currentPostalCode: current?.postalCode ?? '',
        currentCity: current?.city ?? '',
        currentCountry: current?.country ?? '',
        homeStreet: home?.street ?? '',
        homeHouseNumber: home?.houseNumber ?? '',
        homePostalCode: home?.postalCode ?? '',
        homeCity: home?.city ?? '',
        homeCountry: home?.country ?? '',
    };
}

type FormSnapshot = ReturnType<typeof buildSnapshot>;

export const NawFieldsEditor: React.FC<Props> = ({ worker, onSaved }) => {
    // Server-side readiness gate (bot or recruiter): protected BSN, ID and VCA
    // fields must all be populated. Whether the form starts open mirrors that gate.
    const isReady = useMemo(
        () =>
            (Boolean(worker.hasBsn) || Boolean(worker.maskedBsn))
            && !!worker.idDocumentType
            && !!worker.idDocumentNumber
            && !!worker.idDocumentValidFrom
            && !!worker.idDocumentValidUntil
            && !!worker.vcaType
            && !!worker.vcaNumber
            && !!worker.vcaValidFrom
            && !!worker.vcaValidUntil,
        [worker],
    );
    const [expanded, setExpanded] = useState<boolean>(!isReady);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Baseline snapshot the form was opened with. Updated whenever the worker
    // prop changes (e.g. after save → parent refetch). The Save button stays
    // disabled until the current values differ from this snapshot — guards
    // against pointless write churn AND the "edited a field then put the
    // original value back" false-positive.
    const [initialSnapshot, setInitialSnapshot] = useState<FormSnapshot>(() => buildSnapshot(worker));

    const [bsn, setBsn] = useState<string>(initialSnapshot.bsn);
    const [idType, setIdType] = useState<IdType | ''>(initialSnapshot.idType);
    const [idNumber, setIdNumber] = useState<string>(initialSnapshot.idNumber);
    const [idValidUntil, setIdValidUntil] = useState<string>(initialSnapshot.idValidUntil);
    const [vcaType, setVcaType] = useState<VcaSubType | ''>(initialSnapshot.vcaType);
    const [vcaNumber, setVcaNumber] = useState<string>(initialSnapshot.vcaNumber);
    const [vcaValidUntil, setVcaValidUntil] = useState<string>(initialSnapshot.vcaValidUntil);

    const [currentStreet, setCurrentStreet] = useState<string>(initialSnapshot.currentStreet);
    const [currentHouseNumber, setCurrentHouseNumber] = useState<string>(initialSnapshot.currentHouseNumber);
    const [currentPostalCode, setCurrentPostalCode] = useState<string>(initialSnapshot.currentPostalCode);
    const [currentCity, setCurrentCity] = useState<string>(initialSnapshot.currentCity);
    const [currentCountry, setCurrentCountry] = useState<string>(initialSnapshot.currentCountry);
    const [homeStreet, setHomeStreet] = useState<string>(initialSnapshot.homeStreet);
    const [homeHouseNumber, setHomeHouseNumber] = useState<string>(initialSnapshot.homeHouseNumber);
    const [homePostalCode, setHomePostalCode] = useState<string>(initialSnapshot.homePostalCode);
    const [homeCity, setHomeCity] = useState<string>(initialSnapshot.homeCity);
    const [homeCountry, setHomeCountry] = useState<string>(initialSnapshot.homeCountry);

    // Re-sync local state when the worker prop changes (e.g. after bot writes
    // arrive via the parent's refresh, or after this component's own save).
    // Both the form state AND the baseline snapshot reset, so the next edit
    // diff is against the fresh server-truth values.
    useEffect(() => {
        const snap = buildSnapshot(worker);
        setInitialSnapshot(snap);
        setBsn(snap.bsn);
        setIdType(snap.idType);
        setIdNumber(snap.idNumber);
        setIdValidUntil(snap.idValidUntil);
        setVcaType(snap.vcaType);
        setVcaNumber(snap.vcaNumber);
        setVcaValidUntil(snap.vcaValidUntil);
        setCurrentStreet(snap.currentStreet);
        setCurrentHouseNumber(snap.currentHouseNumber);
        setCurrentPostalCode(snap.currentPostalCode);
        setCurrentCity(snap.currentCity);
        setCurrentCountry(snap.currentCountry);
        setHomeStreet(snap.homeStreet);
        setHomeHouseNumber(snap.homeHouseNumber);
        setHomePostalCode(snap.homePostalCode);
        setHomeCity(snap.homeCity);
        setHomeCountry(snap.homeCountry);
    }, [worker]);

    const currentSnapshot: FormSnapshot = {
        bsn,
        idType,
        idNumber,
        idValidUntil,
        vcaType,
        vcaNumber,
        vcaValidUntil,
        currentStreet,
        currentHouseNumber,
        currentPostalCode,
        currentCity,
        currentCountry,
        homeStreet,
        homeHouseNumber,
        homePostalCode,
        homeCity,
        homeCountry,
    };

    // Deep-equal via JSON.stringify on the flat snapshot shape (no nested
    // objects, no functions) — keeps this lib-free and trivially correct.
    const isDirty = useMemo(
        () => JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot),
        [currentSnapshot, initialSnapshot],
    );

    const bsnError = bsn.length > 0 && !isBsnValid(bsn);

    const handleSave = async () => {
        if (bsnError) {
            setError('BSN must be exactly 9 digits.');
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            const homeAddr =
                homeStreet || homePostalCode || homeCity || homeCountry || homeHouseNumber
                    ? {
                          street: homeStreet || null,
                          houseNumber: homeHouseNumber || null,
                          postalCode: homePostalCode || null,
                          city: homeCity || null,
                          country: homeCountry || null,
                      }
                    : null;
            const currentAddr =
                currentStreet || currentPostalCode || currentCity || currentCountry || currentHouseNumber
                    ? {
                          street: currentStreet || null,
                          houseNumber: currentHouseNumber || null,
                          postalCode: currentPostalCode || null,
                          city: currentCity || null,
                          country: currentCountry || null,
                      }
                    : null;

            const payload: UpdateWorkerRequest = {
                workerId: worker.workerId,
                ...(bsn.trim() ? { bsn: bsn.trim() } : {}),
                idDocumentType: (idType || null) as UpdateWorkerRequest['idDocumentType'],
                idDocumentNumber: idNumber.trim() || null,
                idDocumentValidUntil: idValidUntil || null,
                vcaType: (vcaType || null) as UpdateWorkerRequest['vcaType'],
                vcaNumber: vcaNumber.trim() || null,
                vcaValidUntil: vcaValidUntil || null,
                homeAddress: homeAddr,
                currentAddress: currentAddr,
            };
            await updateWorkerRequest(payload);
            setSuccess(true);
            onSaved();
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save NAW fields');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper id="naw-fields-editor" variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <EditIcon color="primary" />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    NAW fields
                </Typography>
                {isReady ? (
                    <Typography variant="caption" color="success.main">
                        All required fields set
                    </Typography>
                ) : (
                    <Typography variant="caption" color="warning.main">
                        Fill in to enable NAW generation
                    </Typography>
                )}
                <Tooltip
                    title={expanded ? 'Collapse' : 'Expand'}
                    children={(
                        <IconButton size="small" onClick={() => setExpanded((s) => !s)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    )}
                />
            </Stack>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                            Saved.
                        </Alert>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Identification (required)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="BSN (9 digits)"
                                value={bsn}
                                onChange={(e) => setBsn(e.target.value)}
                                error={bsnError}
                                helperText={bsnError ? 'BSN must be exactly 9 digits' : worker.maskedBsn ?? ' '}
                                inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel children="ID document type" />
                                <Select
                                    label="ID document type"
                                    value={idType}
                                    onChange={(e) => setIdType(e.target.value as IdType | '')}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {ID_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="ID document number"
                                value={idNumber}
                                onChange={(e) => setIdNumber(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Valid until"
                                value={idValidUntil}
                                onChange={(e) => setIdValidUntil(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        VCA safety passport (required)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel children="VCA type" />
                                <Select
                                    label="VCA type"
                                    value={vcaType}
                                    onChange={(e) => setVcaType(e.target.value as VcaSubType | '')}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {VCA_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="VCA number"
                                value={vcaNumber}
                                onChange={(e) => setVcaNumber(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} />
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Valid until"
                                value={vcaValidUntil}
                                onChange={(e) => setVcaValidUntil(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Current address (where the worker lives now)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField fullWidth size="small" label="Street"
                                value={currentStreet} onChange={(e) => setCurrentStreet(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="House number"
                                value={currentHouseNumber} onChange={(e) => setCurrentHouseNumber(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Postal code"
                                value={currentPostalCode} onChange={(e) => setCurrentPostalCode(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="City"
                                value={currentCity} onChange={(e) => setCurrentCity(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Country"
                                value={currentCountry} onChange={(e) => setCurrentCountry(e.target.value)} />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Home country address (where the worker is from)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField fullWidth size="small" label="Street"
                                value={homeStreet} onChange={(e) => setHomeStreet(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="House number"
                                value={homeHouseNumber} onChange={(e) => setHomeHouseNumber(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Postal code"
                                value={homePostalCode} onChange={(e) => setHomePostalCode(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="City"
                                value={homeCity} onChange={(e) => setHomeCity(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Country"
                                value={homeCountry} onChange={(e) => setHomeCountry(e.target.value)} />
                        </Grid>
                    </Grid>

                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                        {!isDirty && (
                            <Typography variant="caption" color="text.secondary">
                                No changes to save
                            </Typography>
                        )}
                        <Tooltip
                            title={!isDirty ? 'No changes since the last save' : bsnError ? 'Fix the BSN format first' : ''}
                            arrow
                            children={(
                                <span>
                                    <Button
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                        onClick={handleSave}
                                        disabled={saving || bsnError || !isDirty}
                                    >
                                        Save NAW fields
                                    </Button>
                                </span>
                            )}
                        />
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
};
